'use client';

import { useTranslations } from 'next-intl';
import { usePathname } from '@/lib/app-router';
import { useAuthStore } from '@/stores/user.store';
import { useThemeStore } from '@/stores/theme.store';
import { FullScreenLoader, FullScreenError } from '@/components/ui/loaders';
import { computeRequiresAuth } from '@/lib/constants/auth.constants';

/**
 * App-wide boot gate. Holds a full-screen loader on EVERY page (public and
 * authenticated alike) until the auth state has been resolved from the
 * refresh-token exchange, then renders the page. This keeps a single, consistent
 * boot experience and prevents any page from flashing a guest UI (e.g. a "Sign
 * In" button) before flipping to the signed-in state once hydration completes.
 * (PB-3288)
 *
 * Phases:
 * - `!isAuthInitialized` — the SSR refresh-token result hasn't been applied to
 *   the store yet: we don't know if the user is logged in. Show the loader.
 * - authenticated but no profile yet — keep the loader while it loads; if the
 *   fetch has finished and still failed, show a bounded, retryable error instead
 *   of spinning forever.
 * - guest on a route that requires auth — keep the loader while
 *   ClientRouteGuardProvider redirects to /login (so the gated page never flashes).
 * - otherwise (guest on a public route, or authenticated with a loaded profile) —
 *   render the page.
 *
 * This is the single authority for the full-screen boot loader; route guards
 * (ClientRouteGuardProvider) only perform redirects, and the shared
 * `computeRequiresAuth` rule keeps both in sync.
 */
export function AppBootGate({ children }: { children: React.ReactNode }) {
  const isAuthInitialized = useAuthStore((s) => s.isAuthInitialized);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const isLoadingUser = useAuthStore((s) => s.isLoadingUser);
  const fetchUser = useAuthStore((s) => s.fetchUser);
  const logo = useThemeStore((s) => s.theme?.logo);
  const disableGuestBrowsing = useThemeStore((s) => s.theme?.disableGuestBrowsing ?? false);
  const pathname = usePathname();
  const requiresAuth = computeRequiresAuth(pathname, disableGuestBrowsing);
  const tErrors = useTranslations('Errors');
  const tCommon = useTranslations('Common');

  // Auth state not resolved yet — the refresh-token exchange decides guest vs
  // authenticated, so wait for it before showing anything.
  if (!isAuthInitialized) {
    return <FullScreenLoader logo={logo} />;
  }

  // Authenticated, but the profile hasn't arrived yet.
  if (isAuthenticated && !user) {
    if (isLoadingUser) {
      return <FullScreenLoader logo={logo} />;
    }
    // Fetch finished with no user — surface a bounded, recoverable error rather
    // than an indefinite loader.
    return (
      <FullScreenError
        title={tErrors('accountLoadFailedTitle')}
        message={tErrors('accountLoadFailedBody')}
        retryLabel={tCommon('tryAgain')}
        onRetry={fetchUser}
      />
    );
  }

  // Guest on a route that requires auth: ClientRouteGuardProvider is redirecting
  // to /login. Hold the loader through that (post-render) redirect window instead
  // of flashing the gated page/shell. (PB-3288)
  if (!isAuthenticated && requiresAuth) {
    return <FullScreenLoader logo={logo} />;
  }

  return <>{children}</>;
}
